import { AccountType } from '@/data/usecases/account/account-type'
import { ContractedPlanLicense } from '@/data/usecases/plan-license/contracted-plan-license'
import { DocumentModel } from '@/domain/models/document/document-model'
import { DocumentType } from '@/domain/models/document/document-type'
import { Account } from '@/domain/usecases'
import { AccountBenefit } from '@/domain/usecases/account/account-benefit'
import { AccountProfessional } from '@/domain/usecases/account/account-professional'
import { CompanyRefundProgram } from '@/domain/usecases/company/company-refund-program'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { QueryClient } from '@tanstack/react-query'
import {
  UserProfileLoaderBenefitOptionsType,
  UserProfileLoaderDataType,
  UserProfileLoaderProfessionalOptionsType
} from '@/main/routes/data-loaders/user/user-profile'
import { queryClient } from '@/infra/http'

export type CompanyManagerCreateUserProfileLoaderDataType = UserProfileLoaderDataType

/**
 * Loads data for creating a new user profile as company manager
 * Returns empty profile data with options for creating a new collaborator
 */
export async function loadCompanyManagerCreateUserProfile(
  account: Account.Model
): Promise<CompanyManagerCreateUserProfileLoaderDataType> {
  const accountProfile = getAccountProfileData(account)

  const [accountProfessional, { accountBenefits, benefitsOptions }, professionalOptions] =
    await Promise.all([
      getAccountProfessional(account),
      getAccountBenefits(accountProfile, queryClient),
      getProfessionalOptions(account, queryClient)
    ])

  return {
    accountProfile,
    accountProfessional,
    accountBenefits,
    benefitsOptions,
    professionalOptions
  }
}

function getAccountProfileData(account: Account.Model): Account.ProfileModel {
  return {
    id: '',
    accountType: AccountType.Enum.COLLABORATOR,
    document: new DocumentModel(DocumentType.Enum.CPF, ''),
    name: '',
    email: '',
    onboardingCompleted: false,
    onboardingCompletedAt: null,
    roles: [],
    company: account.company,
    addons: account.addons,
    preferenceLanguage: ''
  }
}

async function getProfessionalOptions(
  account: Account.Model,
  queryClient: QueryClient
): Promise<UserProfileLoaderProfessionalOptionsType | undefined> {
  if (AccountType.Enum.DEPENDENT.is(account)) return undefined

  const module = await import('@/main/factories/usecases')
  const makeRemoteOnboardingOptions = module.makeRemoteOnboardingOptions as (
    queryClient: QueryClient
  ) => OnboardingOptions
  const remoteOnboardingOptions = makeRemoteOnboardingOptions(queryClient)
  const data = await remoteOnboardingOptions.getStep1()

  return {
    areas: data.professionalAreas,
    seniorities: data.professionalSeniorities
  }
}

function getAccountProfessional(account: Account.Model): AccountProfessional.Model {
  const { company } = account
  return {
    company: company,
    requiresApproval: company.requiresApproval
  }
}

async function getCompanyRefundPrograms(
  accountProfile: Account.ProfileModel,
  queryClient: QueryClient
): Promise<CompanyRefundProgram.ListModel> {
  if (!accountProfile.company.requiresApproval) return []

  const module = await import('@remote/company/remote-company-refund-program-factory')
  const makeRemote = module.makeRemoteCompanyRefundProgram
  const remote = makeRemote(accountProfile.company.uniqueId, queryClient)
  return remote.loader()
}

async function getBenefitsOptions(
  accountProfile: Account.ProfileModel,
  queryClient: QueryClient
): Promise<AccountBenefit.OptionsModel> {
  const [licenses, refundPrograms] = await Promise.all([
    import('@remote/account/company-manager/remote-company-manager-plan-license-factory')
      .then(module => module.makeRemoteCompanyPlanLicense)
      .then(async makeRemotePlanLicense => {
        const remote = makeRemotePlanLicense(accountProfile.company.uniqueId, queryClient)
        return remote.loader()
      }),
    getCompanyRefundPrograms(accountProfile, queryClient)
  ])

  return {
    passPlans: licenses
      .filter(license => accountProfile.accountType === license.accountType)
      .map(license => ({
        id: license.id,
        name: license.name,
        contracted: !!license.available
      })) as ContractedPlanLicense.ListModel,
    refundPrograms
  }
}

async function getAccountBenefits(
  accountProfile: Account.ProfileModel,
  queryClient: QueryClient
): Promise<UserProfileLoaderBenefitOptionsType> {
  const options = await getBenefitsOptions(accountProfile, queryClient)
  const { refundPrograms } = options

  return {
    benefitsOptions: options,
    accountBenefits: {
      contractedPlan: {
        plan: null
      },
      addons: {
        refund: {
          enabled: !!refundPrograms?.length
        } as Account.AddonModel
      }
    }
  }
}
